import { isHostedRuntime, isLoopbackOrigin } from './app-origin';

/**
 * Suppression reason for a developer machine whose app URL is loopback. It
 * stops delivery only: it is not evidence of a governed non-production host,
 * so auth limiters must not treat it as permission to fail open.
 */
export const LOCAL_LOOPBACK_SUPPRESSION_REASON = 'host:loopback';

const NON_PRODUCTION_SUPABASE_REFS = new Set([
  'yqovcezkrnpzzzigzeqs', // legacy demo/staging
  'lysvpptstfobqwdhvopi', // shared development
  'djoztpurhhwbpuzupoav', // governed UAT
  'htibqcdrtssafqxjcblz', // retired UAT-class project; still suppress outbound delivery
]);
const NON_PRODUCTION_HOSTS = new Set([
  'demo.statura.care',
  'familydemo.statura.care',
  'dev.statura.care',
  'family-dev.statura.care',
  'uat.statura.care',
  'family-uat.statura.care',
]);
const NON_PRODUCTION_LOGICAL_ENVIRONMENTS = new Set(['dev', 'development', 'preview', 'uat']);
const PRODUCTION_INTERNAL_ORIGINS = new Set([
  'https://app.statura.care',
  'https://family.statura.care',
  'https://xaxycjprriboclvfoxbe.supabase.co',
]);

// RFC 6761 reserved TLDs that must not appear in real email routing.
// RFC 2606 reserved second-level domains that are documentation-only.
// Any recipient at one of these is non-routable and must NOT be sent via SMTP —
// Resend/external providers will hard-bounce, eating sender reputation.
const NON_ROUTABLE_EMAIL_TLDS = new Set(['test', 'invalid', 'localhost', 'example']);
const NON_ROUTABLE_SLDS = new Set(['example.com', 'example.org', 'example.net']);

type EnvironmentLike = Record<string, string | undefined>;
type ExternalTransportInput = string | URL | { url: string };

const EXTERNAL_TRANSPORT_GUARD_MARKER = Symbol.for('statura.external-transport-fetch-guard');

function isTruthy(value: string | undefined): boolean {
  if (!value) return false;
  return ['1', 'true', 'yes', 'on'].includes(value.trim().toLowerCase());
}

function extractHostname(value: string | undefined): string | null {
  if (!value) return null;

  try {
    return new URL(value).hostname.toLowerCase();
  } catch {
    return null;
  }
}

function isDemoOrStagingHost(hostname: string | null): boolean {
  if (!hostname) return false;
  if (NON_PRODUCTION_HOSTS.has(hostname)) return true;
  return hostname.includes('staging') || hostname.includes('preview');
}

export function getOutboundCommunicationSuppressionReason(
  env: EnvironmentLike = process.env,
): string | null {
  if (isTruthy(env.SUPPRESS_OUTBOUND_COMMUNICATIONS)) {
    return 'SUPPRESS_OUTBOUND_COMMUNICATIONS';
  }

  if (env.STATURA_EXTERNAL_INTEGRATIONS_MODE?.trim().toLowerCase() === 'disabled') {
    return 'STATURA_EXTERNAL_INTEGRATIONS_MODE:disabled';
  }

  const logicalEnvironment = env.STATURA_ENVIRONMENT?.trim().toLowerCase();
  if (logicalEnvironment && NON_PRODUCTION_LOGICAL_ENVIRONMENTS.has(logicalEnvironment)) {
    return `statura:${logicalEnvironment}`;
  }

  const vercelEnv = env.VERCEL_ENV?.toLowerCase();
  if (vercelEnv && vercelEnv !== 'production') {
    return `vercel:${vercelEnv}`;
  }

  const appHost = extractHostname(env.APP_URL ?? env.NEXT_PUBLIC_APP_URL);
  if (isDemoOrStagingHost(appHost)) {
    return `host:${appHost}`;
  }

  // A developer machine (not a hosted runtime) whose app URL is loopback must
  // not deliver real communications: every link it renders points at the
  // recipient's own machine, and its data plane may be production. Local runs
  // against production sent localhost-linked invites, resets and alerts to real
  // staff from June to September 2026.
  if (!isHostedRuntime(env) && isLoopbackOrigin(env.APP_URL ?? env.NEXT_PUBLIC_APP_URL)) {
    return LOCAL_LOOPBACK_SUPPRESSION_REASON;
  }

  const portalHost = extractHostname(env.NEXT_PUBLIC_CLIENT_PORTAL_URL);
  if (isDemoOrStagingHost(portalHost)) {
    return `host:${portalHost}`;
  }

  const supabaseHost = extractHostname(env.NEXT_PUBLIC_SUPABASE_URL);
  const supabaseRef = supabaseHost?.match(/^([a-z]{20})\.supabase\.co$/)?.[1];
  if (supabaseRef && NON_PRODUCTION_SUPABASE_REFS.has(supabaseRef)) {
    return `supabase:${supabaseRef}`;
  }

  return null;
}

export function shouldSuppressOutboundCommunications(env: EnvironmentLike = process.env): boolean {
  return getOutboundCommunicationSuppressionReason(env) !== null;
}

function destinationUrl(input: ExternalTransportInput): URL | null {
  const value =
    typeof input === 'string' || input instanceof URL
      ? input
      : typeof input?.url === 'string'
        ? input.url
        : null;
  if (value === null) return null;
  if (typeof value === 'string' && value.startsWith('/')) return null;
  try {
    return value instanceof URL ? value : new URL(value);
  } catch {
    return null;
  }
}

function configuredInternalOrigins(env: EnvironmentLike): Set<string> {
  const origins = new Set<string>();
  for (const value of [
    env.APP_URL,
    env.NEXT_PUBLIC_APP_URL,
    env.NEXT_PUBLIC_CLIENT_PORTAL_URL,
    env.CLIENT_PORTAL_URL,
  ]) {
    if (!value) continue;
    try {
      origins.add(new URL(value).origin);
    } catch {
      // Invalid configuration is not an allowlist entry.
    }
  }
  // Platform infrastructure the app itself depends on is not an outbound communication:
  // the authentication rate limiter's Upstash REST endpoint stays reachable under suppression,
  // otherwise every hosted non-production environment fails closed at login (found 6 Sep 2026).
  if (env.UPSTASH_REDIS_REST_URL) {
    try {
      origins.add(new URL(env.UPSTASH_REDIS_REST_URL).origin);
    } catch {
      // Invalid configuration is not an allowlist entry.
    }
  }
  if (env.VERCEL_URL) {
    try {
      origins.add(new URL(`https://${env.VERCEL_URL}`).origin);
    } catch {
      // Invalid configuration is not an allowlist entry.
    }
  }
  const expectedSupabaseRef = env.STATURA_EXPECTED_SUPABASE_PROJECT_REF?.trim();
  if (expectedSupabaseRef && /^[a-z0-9]{20}$/.test(expectedSupabaseRef)) {
    origins.add(`https://${expectedSupabaseRef}.supabase.co`);
  }
  if (env.NEXT_PUBLIC_SUPABASE_URL) {
    try {
      const configuredSupabase = new URL(env.NEXT_PUBLIC_SUPABASE_URL);
      const configuredRef = configuredSupabase.hostname.match(
        /^([a-z0-9]{20})\.supabase\.co$/,
      )?.[1];
      if (configuredRef && (!expectedSupabaseRef || configuredRef === expectedSupabaseRef)) {
        origins.add(configuredSupabase.origin);
      }
    } catch {
      // Invalid configuration is not an allowlist entry.
    }
  }
  return origins;
}

/**
 * Internal application/Supabase traffic remains available when external
 * transports are disabled. The guard deliberately does not become a generic
 * network kill-switch: it protects external adapters while preserving the
 * platform's own data plane and loopback mocks.
 */
export function isInternalPlatformTransportDestination(
  input: ExternalTransportInput,
  env: EnvironmentLike = process.env,
): boolean {
  if (typeof input === 'string' && input.startsWith('/')) return true;
  const destination = destinationUrl(input);
  if (!destination || !['http:', 'https:'].includes(destination.protocol)) return false;
  const hostname = destination.hostname.toLowerCase();
  if (['localhost', '127.0.0.1', '::1', '[::1]', '10.0.2.2'].includes(hostname)) return true;
  if (PRODUCTION_INTERNAL_ORIGINS.has(destination.origin)) return false;
  return configuredInternalOrigins(env).has(destination.origin);
}

export function getExternalTransportSuppressionReason(
  input: ExternalTransportInput,
  env: EnvironmentLike = process.env,
): string | null {
  if (isInternalPlatformTransportDestination(input, env)) return null;
  return getOutboundCommunicationSuppressionReason(env);
}

export class ExternalTransportSuppressedError extends Error {
  readonly code = 'external_transport_suppressed';

  constructor(readonly reason: string) {
    super(`External transport suppressed: ${reason}`);
    this.name = 'ExternalTransportSuppressedError';
  }
}

export function assertExternalTransportAllowed(
  input: ExternalTransportInput,
  env: EnvironmentLike = process.env,
): void {
  const reason = getExternalTransportSuppressionReason(input, env);
  if (reason) throw new ExternalTransportSuppressedError(reason);
}

export function createExternalTransportGuardedFetch(
  baseFetch: typeof globalThis.fetch,
  env: EnvironmentLike = process.env,
): typeof globalThis.fetch {
  const guardedFetch = (async (input, init) => {
    assertExternalTransportAllowed(input, env);
    return baseFetch(input, init);
  }) as typeof globalThis.fetch & { [EXTERNAL_TRANSPORT_GUARD_MARKER]?: true };
  guardedFetch[EXTERNAL_TRANSPORT_GUARD_MARKER] = true;
  return guardedFetch;
}

/** Install once per Next server runtime. Production-like environments are unchanged. */
export function installExternalTransportFetchGuard(env: EnvironmentLike = process.env): boolean {
  if (!getOutboundCommunicationSuppressionReason(env)) return false;
  const currentFetch = globalThis.fetch as typeof globalThis.fetch & {
    [EXTERNAL_TRANSPORT_GUARD_MARKER]?: true;
  };
  if (currentFetch[EXTERNAL_TRANSPORT_GUARD_MARKER]) return false;
  globalThis.fetch = createExternalTransportGuardedFetch(currentFetch.bind(globalThis), env);
  return true;
}

/**
 * Returns true for email addresses whose TLD is reserved for testing /
 * documentation per RFC 6761 / RFC 2606. These addresses must never be
 * dispatched via SMTP — providers like Resend hard-bounce them, eating
 * sender-reputation score.
 *
 * Examples returning true:
 *   user@example.com, user@example.org, user@example.net (RFC 2606 §3)
 *   user@anything.test, user@anything.example, user@anything.invalid,
 *   user@anything.localhost (RFC 6761)
 *
 * Per-recipient guard — orthogonal to the env/host-based
 * `getOutboundCommunicationSuppressionReason`. Both checks fire in
 * `sendTemplatedEmail` and downstream channels.
 */
export function isNonRoutableEmailRecipient(email: string | null | undefined): boolean {
  if (!email) return false;
  const trimmed = email.trim().toLowerCase();
  const at = trimmed.lastIndexOf('@');
  if (at < 0 || at === trimmed.length - 1) return false;
  const domain = trimmed.slice(at + 1);
  if (NON_ROUTABLE_SLDS.has(domain)) return true;
  const lastDot = domain.lastIndexOf('.');
  if (lastDot < 0) return false;
  const tld = domain.slice(lastDot + 1);
  return NON_ROUTABLE_EMAIL_TLDS.has(tld);
}

/** Shared row policy for server-side portal dispatch and Auth credential issuance. */
export function tenantSuppressesExternalDispatch(
  row:
    | {
        is_demo?: boolean | null;
        external_egress_blocked?: boolean | null;
        brand_config?: Record<string, unknown> | null;
      }
    | null
    | undefined,
): boolean {
  return (
    !row ||
    row.external_egress_blocked === true ||
    row.is_demo === true ||
    row.brand_config?.suppressOutboundCommunications === true
  );
}

export function isInactiveCommunicationRecipient(row: {
  is_active?: boolean | null;
  status?: string | null;
}): boolean {
  return row.is_active === false || row.status?.toUpperCase() === 'INACTIVE';
}
